import React, { useEffect } from "react";
import { useTable, usePagination } from "react-table";
import createPagination from "../Hooks/usePagination";
import { MdCompareArrows } from "react-icons/md";

let reload = false;
let page = 1;

const Table = ({
    columns,
    data,
    updatePagination,
    loading,
    paginationState,
    pageCount: controlledPageCount,
    totalCount,
    sortable,
    hidePagination,
    updateLoaded,
}) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        gotoPage,
        pageCount,
        canNextPage,
        canPreviousPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            manualPagination: true,
            pageCount: controlledPageCount,
            initialState: {
                pageIndex: 0,
            },
        },
        usePagination
    );

    useEffect(() => {
        if (reload) {
            updatePagination((prev) => ({
                ...prev,
                page: pageIndex,
            }));

            updateLoaded(false);
            reload = false;
        }
    }, [updatePagination, updateLoaded, pageIndex, pageSize]);

    const { pagination } = createPagination({
        numberOfButtons: 2,
        currentPage: paginationState ? paginationState.page : pageIndex + 1,
        totalPages: paginationState
            ? paginationState.totalPages
                ? paginationState.totalPages
                : controlledPageCount
            : controlledPageCount,
    });

    return (
        <div className="table-wrapper block max-w-full overflow-x-auto overflow-y-hidden" style={{ backgroundColor: '#FFFFFF', borderRadius: '0 0 8px 8px' }}>
            <table className="w-full border-collapse" {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr style={{ backgroundColor: '#000000' }} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th
                                    className="text-center p-4 text-sm font-medium"
                                    style={{ color: '#FFFFFF' }}
                                    data-column={column.id}
                                    {...column.getHeaderProps()}
                                >
                                    <div
                                        className={`flex items-center justify-center`}
                                    >
                                        <div className="whitespace-no-wrap">
                                            {column.render("Header")}
                                        </div>
                                        {sortable && (
                                            <div className="cursor-pointer ml-2">
                                                {sortable.includes(
                                                    column.id
                                                ) && (
                                                    <MdCompareArrows
                                                        onClick={() => {
                                                            updatePagination(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    by: column.id,
                                                                    dir:
                                                                        prev.dir ===
                                                                        "asc"
                                                                            ? "desc"
                                                                            : "asc",
                                                                    page: 1,
                                                                })
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, index) => {
                        prepareRow(row);
                        const isCancelled = row.original && row.original.status === "cancelled";
                        const isDeposit = row.original && row.original.status === "deposit";
                        
                        let defaultBgColor, cancelledBgColor, depositBgColor, hoverBgColor;
                        
                        if (isCancelled) {
                            defaultBgColor = index % 2 === 0 ? '#FEE2E2' : '#FECACA';
                            hoverBgColor = '#FCA5A5';
                        } else if (isDeposit) {
                            defaultBgColor = index % 2 === 0 ? '#DBEAFE' : '#BFDBFE';
                            hoverBgColor = '#93C5FD';
                        } else {
                            defaultBgColor = index % 2 === 0 ? '#E6F7E6' : '#D9F0D9';
                            hoverBgColor = '#C8E6C8';
                        }
                        
                        return (
                            <tr
                                className="transition-colors"
                                style={{
                                    backgroundColor: defaultBgColor,
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = hoverBgColor;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = defaultBgColor;
                                }}
                                {...row.getRowProps()}
                            >
                                {row.cells.map((cell) => {
                                    return (
                                        <td
                                            className="text-center p-4 text-sm border-t"
                                            style={{ color: '#333333' }}
                                            data-column={cell.column.id}
                                            {...cell.getCellProps()}
                                        >
                                            {cell.render("Cell")}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {!hidePagination && (
                <div className="p-4 bg-cool-gray border-t font-semibold text-lynch text-xs">
                    <ul className="flex justify-end pr-5">
                        <li
                            className="p-2 cursor-pointer"
                            onClick={() => {
                                page = 1;
                                gotoPage(1);
                                reload = true;
                            }}
                            disabled={!canPreviousPage}
                        >
                            First
                        </li>
                        <li
                            className="p-2 cursor-pointer"
                            onClick={() => {
                                gotoPage(pagination[0]);
                                page = page - 1
                                reload = true;
                            }}
                        >
                            Previous
                        </li>
                        {pagination.map((list) => (
                            <li
                                className={`p-2 px-4 cursor-pointer ${
                                    list === page
                                        ? "bg-darkblue text-white"
                                        : "text-xs"
                                }`}
                                key={list}
                                onClick={() => {
                                    gotoPage(list);
                                    reload = true;
                                    page = list
                                }}
                            >
                                {list}
                            </li>
                        ))}
                        <li
                            className="p-2 cursor-pointer"
                            onClick={() => {
                                gotoPage(page === 1 ? pagination[1] : pagination[2]);
                                page = page + 1
                                reload = true;
                            }}
                        >
                            Next
                        </li>
                        <li
                            className="p-2 cursor-pointer"
                            onClick={() => {
                                gotoPage(pageCount - 1);
                                page = (pageCount - 1)
                                reload = true;
                            }}
                            disabled={!canNextPage}
                        >
                            Last
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Table;
